import {createStyles, Select} from '@mantine/core';
import React, {forwardRef} from 'react';

import {
  ANALYSIS_VISUALIZATION_TYPE_TO_ICON,
  ANALYSIS_VISUALIZATION_TYPE_TO_READABLE,
  getAnalysisOnlyChartViewTypes,
} from 'analysis-tool/charting/AnalysisChartingConstants';
import {AnalysisVisualizationType} from 'analysis-tool/ui/charting/ChartingTypes';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

/** The set of chart types available in the switcher, plus TABLE. */
export type ChartTypeSwitcherValue = AnalysisVisualizationType;

interface ChartTypeSwitcherProps {
  currentType: ChartTypeSwitcherValue;
  onChange: (newType: ChartTypeSwitcherValue) => void;
  isDisabled: boolean;
}

/** Types excluded from the switcher (not supported as chart creation targets). */
const EXCLUDED_TYPES = new Set<AnalysisVisualizationType>([AnalysisVisualizationType.CONTOUR_PLOT]);

/** Returns the ordered list of chart types for the switcher dropdown, including TABLE. */
function getOrderedTypes(): Array<AnalysisVisualizationType> {
  return [AnalysisVisualizationType.TABLE, ...getAnalysisOnlyChartViewTypes()];
}

const useStyles = createStyles({
  selectItemRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  selectItemIcon: {
    color: GRAY_DARK,
  },
  root: {
    marginBottom: 12,
  },
});

interface SelectItemProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  value: string;
}

/**
 * Returns the icon name for the given select item value.
 * The value is always an AnalysisVisualizationType string since we control the data,
 * but Mantine's Select types it as a generic string.
 */
function getIconForValue(
  value: string
): (typeof ANALYSIS_VISUALIZATION_TYPE_TO_ICON)[AnalysisVisualizationType] {
  return ANALYSIS_VISUALIZATION_TYPE_TO_ICON[value as AnalysisVisualizationType];
}

// Pattern for custom Mantine Select items
// See https://v6.mantine.dev/core/select/#custom-item-component
const ChartTypeSelectItem = forwardRef<HTMLDivElement, SelectItemProps>(
  ({label, value, ...rest}: SelectItemProps, ref) => {
    const {classes} = useStyles();
    return (
      <div ref={ref} {...rest}>
        <div className={classes.selectItemRow}>
          <BenchlingIcon className={classes.selectItemIcon} icon={getIconForValue(value)} />
          <span>{label}</span>
        </div>
      </div>
    );
  }
);
// Required for forwardRef components to show a useful name in React DevTools
ChartTypeSelectItem.displayName = 'ChartTypeSelectItem';

function getSelectData(): Array<{value: string; label: string}> {
  return getOrderedTypes()
    .filter((type) => !EXCLUDED_TYPES.has(type))
    .map((type) => ({
      value: type,
      label: ANALYSIS_VISUALIZATION_TYPE_TO_READABLE[type],
    }));
}

export default function ChartTypeSwitcher({
  currentType,
  onChange,
  isDisabled,
}: ChartTypeSwitcherProps): JSX.Element {
  const {classes} = useStyles();
  return (
    <div {...getTestProps({component: 'ChartTypeSwitcher', element: 'root'})} className={classes.root}>
      <Select
        size="md"
        label="Chart type"
        data={getSelectData()}
        value={currentType}
        onChange={(value: string | null) => {
          if (value != null && value !== currentType) {
            onChange(value as ChartTypeSwitcherValue);
          }
        }}
        disabled={isDisabled}
        icon={
          <BenchlingIcon
            className={classes.selectItemIcon}
            icon={ANALYSIS_VISUALIZATION_TYPE_TO_ICON[currentType]}
          />
        }
        itemComponent={ChartTypeSelectItem}
        maxDropdownHeight={400}
        {...getTestProps({component: 'ChartTypeSwitcher', element: 'select'})}
      />
    </div>
  );
}
